import { get } from '../../utils';
import {
  CustomEntitlement,
  CustomSharedEntitlement,
  Leave,
  PersonData,
  SelectedLeavePlan,
} from '../../data-models';
import { NY_PFL_PLANS, TRACKING_PLANS } from './util';
import dayjs from 'dayjs';
import { getLeaveAvailibility } from '../../utils/web-apis-client';

const dateFormat = 'MMM DD, YYYY';
const caclFormat = 'YYYY-MM-DD';

const selectedLeavePlans = (leaves: Leave[]) => {
  const output: SelectedLeavePlan[] = [];
  leaves.forEach((leave) => {
    if (leave.selectedLeavePlans) {
      leave.selectedLeavePlans.forEach((plan) => {
        if (
          !TRACKING_PLANS.find((ele) => ele === plan.longname) &&
          plan.entitlementCalculationPeriod &&
          plan.timeWithinPeriodBasis &&
          plan.timeBasis &&
          plan.timeEntitlement &&
          plan.availabilityPeriodStartDate &&
          plan.availabilityPeriodEndDate
        ) {
          output.push({ ...plan });
        }
      });
    }
  });
  return output;
};

const groupSharedSelectedLeavePlans = (selectedLeaves: SelectedLeavePlan[]) => {
  const map = new Map<string, SelectedLeavePlan[]>();
  selectedLeaves.forEach((leave) => {
    // leave has child node(s); a leave can belong to multiple shared caps
    // (e.g. an overall limit plus a nested sub-limit), so group it under each.
    if (leave.sharedEntitlementPlans?.length) {
      leave.sharedEntitlementPlans.forEach((sharedPlan) => {
        const value = map.get(sharedPlan.id);
        if (value) {
          const checkDupe = value.find(
            (v) => v.leavePlanId === leave.leavePlanId,
          );
          if (!checkDupe) {
            value.push({ ...leave });
            map.set(sharedPlan.id, value);
          }
        } else {
          map.set(sharedPlan.id, [{ ...leave }]);
        }
      });
    } else if (leave.leavePlanId) {
      // some tracking plans have empty sharedEntitlement nodes
      map.set(leave.leavePlanId, [{ ...leave }]);
    }
  });
  return map;
};

const shapeData = (map: Map<string, SelectedLeavePlan[]>) => {
  if (map.size === 0) {
    return [];
  }
  const customData: CustomSharedEntitlement[] = [];
  map.forEach((value, key) => {
    // resolve the shared plan matching this group key (not just index [0]),
    // since a leave can reference multiple shared caps.
    const shared = value
      .map((v) => v.sharedEntitlementPlans?.find((sp) => sp.id === key))
      .find(Boolean);

    // dont nest NY plans or standalone/single-member groups
    if (
      NY_PFL_PLANS.find((e) => e === value[0]?.longname) ||
      value.length <= 1 ||
      !shared
    ) {
      customData.push({
        mainEntitlement: createEntitlement(value[0]),
        entitlements: [],
      });
    }
    // create nested data if leaves have shared plans
    else if (value.length > 1) {
      const temp: CustomEntitlement[] = [];
      value.forEach((plan) => {
        temp.push(createEntitlement(plan));
      });
      customData.push({
        mainEntitlement: {
          id: shared.id,
          name: shared.name,
          availableTime: formatNumber(value[0].projectedAvailableTime),
          periodEndDate: dayjs(value[0].availabilityPeriodEndDate).format(
            dateFormat,
          ),
          calculationPeriod: value[0].entitlementCalculationPeriod,
          timeBasis: shared.timeBasis,
          timeEntitlement: shared.timeEntitlement,
          timeWithinPeriod: shared.timeWithinPeriod,
          timeWithinPeriodBasis: shared.timeWithinPeriodBasis,
          approvedTime: roundToFourDecimals(shared.approvedTime),
          pendingTime: roundToFourDecimals(shared.pendingTime),
          availabilityPeriodStartDate: shared.availabilityPeriodStartDate,
          availabilityPeriodEndDate: shared.availabilityPeriodEndDate,
          projectedAvailableTime: roundToFourDecimals(
            value[0].projectedAvailableTime,
          ),
        },
        entitlements: temp,
      });
    }
  });
  return customData.sort(
    (a, b) => b.entitlements.length - a.entitlements.length,
  );
};

const createEntitlement = (
  selectedLeave: SelectedLeavePlan,
): CustomEntitlement => {
  return {
    id: selectedLeave.leavePlanId ?? '',
    name: selectedLeave.longname ?? '',
    availableTime: formatNumber(selectedLeave.projectedAvailableTime),
    periodEndDate: dayjs(selectedLeave.availabilityPeriodEndDate).format(
      dateFormat,
    ),
    calculationPeriod: selectedLeave.entitlementCalculationPeriod,
    timeBasis: selectedLeave.timeBasis ?? '',
    timeEntitlement: selectedLeave.timeEntitlement,
    timeWithinPeriod: selectedLeave.timeWithinPeriod,
    timeWithinPeriodBasis: selectedLeave.timeWithinPeriodBasis,
    approvedTime: roundToFourDecimals(selectedLeave.approvedTime),
    pendingTime: roundToFourDecimals(selectedLeave.pendingTime),
    availabilityPeriodStartDate: selectedLeave.availabilityPeriodStartDate,
    availabilityPeriodEndDate: selectedLeave.availabilityPeriodEndDate,
    projectedAvailableTime: roundToFourDecimals(
      selectedLeave.projectedAvailableTime,
    ),
  };
};

const roundToFourDecimals = (num: number) => Number.parseFloat(num.toFixed(4));

const formatNumber = (num: number) => roundToFourDecimals(num).toString();

export const getAvailableTime = async (
  personData: PersonData | undefined,
  calculationDate?: string | null,
) => {
  const leaves: Leave[] = get(personData, 'cases.leaves', []);

  if (!leaves) {
    return [];
  }

  const selectedLeaves = selectedLeavePlans(leaves);
  const grouped = groupSharedSelectedLeavePlans(selectedLeaves);
  const shapedData = shapeData(grouped);

  // Create array of promises for parallel API calls
  const updatePromises = shapedData.flatMap((item) => {
    const isRollingBack = item.mainEntitlement.calculationPeriod
      .toLocaleLowerCase()
      .includes('rolling back');

    let dateToUse: string | undefined;
    if (calculationDate) {
      dateToUse = dayjs(calculationDate).format(caclFormat);
    } else if (item.entitlements.length > 0) {
      dateToUse = isRollingBack
        ? dayjs(item.entitlements[0].availabilityPeriodEndDate).format(
            caclFormat,
          )
        : dayjs(item.entitlements[0].availabilityPeriodStartDate).format(
            caclFormat,
          );
    }

    if (!dateToUse) return [];

    const definedDateToUse: string = dateToUse;

    const promises = [
      (async () => {
        const upToDate = await getLeaveAvailibility(
          item.mainEntitlement.id,
          definedDateToUse,
        );

        item.mainEntitlement = {
          ...item.mainEntitlement,
          pendingTime: roundToFourDecimals(upToDate.pendingTime),
          approvedTime: roundToFourDecimals(upToDate.approvedTime),
          availableTime: formatNumber(upToDate.projectedAvailableTime),
          periodEndDate: dayjs(upToDate.availabilityPeriodEndDate).format(
            dateFormat,
          ),
        };
      })(),
    ];

    if (item.entitlements.length > 0) {
      // Update child entitlements
      item.entitlements.forEach((entitlement, index) => {
        promises.push(
          (async () => {
            const upToDate = await getLeaveAvailibility(
              entitlement.id,
              definedDateToUse,
            );

            item.entitlements[index] = {
              ...entitlement,
              pendingTime: roundToFourDecimals(upToDate.pendingTime),
              approvedTime: roundToFourDecimals(upToDate.approvedTime),
              availableTime: formatNumber(upToDate.projectedAvailableTime),
              periodEndDate: dayjs(upToDate.availabilityPeriodEndDate).format(
                dateFormat,
              ),
              projectedAvailableTime: roundToFourDecimals(
                upToDate.projectedAvailableTime,
              ),
            };
          })(),
        );
      });
    }

    return promises;
  });

  await Promise.all(updatePromises);

  shapedData.forEach((item) => {
    if (item.entitlements.length > 1) {
      item.entitlements.sort(
        (a, b) => a.projectedAvailableTime - b.projectedAvailableTime,
      );
    }
  });

  return shapedData;
};
